import { Badge, Card, Col, Form, Modal, Row } from "react-bootstrap";
import { Button } from "./components/Button";
import { useState } from "react";

export interface FileGroup {
    name: string;
    files: File[];
}

interface UploadModalProps {
    show: boolean;
    onHide: () => void;
    onUpload: ( groups: FileGroup[] ) => void;
    datasetId: number;
}

export function UploadModal( { show, onHide, onUpload, datasetId }: UploadModalProps ) {
    const [groups, setGroups] = useState<FileGroup[]>( [] );
    const [dragActive, setDragActive] = useState( false );
    const handleSelect = ( e: React.ChangeEvent<HTMLInputElement> ) => {
        const files = Array.from( e.target.files || [] );
        processFiles( files );
    };

    const processFiles = ( files: File[] ) => {
        const map = new Map<string, File[]>();
        files.forEach( f => {
            const base = f.name.replace( /\.[^/.]+$/, '' );
            if( !map.has( base ) ) {
                map.set( base, [] );
            }
            map.get( base )!.push( f );
        } );
        setGroups( Array.from( map.entries() ).map( ( [name, files] ) => ( { name, files } ) ) );
    };

    const handleDrag = ( e: React.DragEvent ) => {
        e.preventDefault();
        e.stopPropagation();
        if( e.type === "dragenter" || e.type === "dragover" ) {
            setDragActive( true );
        } else if( e.type === "dragleave" ) {
            setDragActive( false );
        }
    };

    const handleDrop = ( e: React.DragEvent ) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive( false );

        const files = Array.from( e.dataTransfer.files );
        processFiles( files );
    };

    const handleUpload = () => {
        onUpload( groups );
        setGroups( [] );
        onHide();
    };

    const handleClose = () => {
        setGroups( [] );
        onHide();
    };

    const removeGroup = ( groupName: string ) => {
        setGroups( groups.filter( g => g.name !== groupName ) );
    };

    const totalFiles = groups.reduce( ( acc, group ) => acc + group.files.length, 0 );

    return (
        <Modal show={show} onHide={handleClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Upload Images to Dataset</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-4">
                    <div
                        className={`border-3 border-dashed rounded p-5 text-center ${
                            dragActive ? 'border-primary bg-light' : 'border-secondary'
                        }`}
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        style={{ minHeight: '200px', cursor: 'pointer' }}
                    >
                        <div className="mb-3">
                            <i className="fas fa-cloud-upload-alt fa-3x text-muted"></i>
                        </div>
                        <h5>Drag and drop files here or click to browse</h5>
                        <p className="text-muted mb-3">
                            You can upload multiple files or select a directory
                        </p>
                        <Form.Control
                            type="file"
                            multiple
                            // @ts-ignore - directory upload
                            webkitdirectory="true"
                            onChange={handleSelect}
                            style={{ display: 'none' }}
                            id="directory-upload"
                        />
                        <Form.Control
                            type="file"
                            multiple
                            onChange={handleSelect}
                            style={{ display: 'none' }}
                            id="file-upload"
                        />
                        <div className="d-flex gap-2 justify-content-center">
                            <Button
                                variant="outline-primary"
                                onClick={() => document.getElementById('file-upload')?.click()}
                            >
                                Select Files
                            </Button>
                            <Button
                                variant="outline-secondary"
                                onClick={() => document.getElementById('directory-upload')?.click()}
                            >
                                Select Directory
                            </Button>
                        </div>
                    </div>
                </div>

                {groups.length > 0 && (
                    <div>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h6>Selected Files</h6>
                            <Badge bg="secondary">{totalFiles} files in {groups.length} groups</Badge>
                        </div>
                        <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            <Row>
                                {groups.map((group, index) => (
                                    <Col md={6} key={group.name} className="mb-3">
                                        <Card className="h-100">
                                            <Card.Header className="d-flex justify-content-between align-items-center py-2">
                                                <small className="fw-bold text-truncate" title={group.name}>
                                                    {group.name}
                                                </small>
                                                <Button
                                                    variant="outline-danger"
                                                    size="sm"
                                                    onClick={() => removeGroup(group.name)}
                                                    className="py-0 px-2"
                                                >
                                                    ×
                                                </Button>
                                            </Card.Header>
                                            <Card.Body className="py-2">
                                                <small className="text-muted">
                                                    {group.files.length} file{group.files.length !== 1 ? 's' : ''}
                                                </small>
                                                <div className="mt-1" style={{ fontSize: '0.75rem' }}>
                                                    {group.files.slice(0, 3).map(f => (
                                                        <div key={f.name} className="text-truncate">
                                                            {f.name}
                                                        </div>
                                                    ))}
                                                    {group.files.length > 3 && (
                                                        <div className="text-muted">
                                                            ... and {group.files.length - 3} more
                                                        </div>
                                                    )}
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Cancel
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleUpload}
                    disabled={groups.length === 0}
                >
                    Upload {totalFiles > 0 && `(${totalFiles} files)`}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}